import React, { useEffect, useState, Fragment } from "react";

import { ReactSVG } from "react-svg";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";

import MedicalHistoryCreate from "../patient/clinical-services/service-steps/clinical-assessment/MedicalHistoryCreate";
import {
  fetchMedicalConditions,
  updateMedicalConditionFromChart,
  updateClinicalService,
  updateMedicationReview,
  fetchClinicalServices,
} from "../../redux/modules/clinical-services/actions";
import NewToolTip from "../common/New-ToolTip";
import ConfirmationModalNew from "../common/ConfirmationModalNew";
import { DeleteOptions } from "./clinical-services/service-steps/clinical-assessment/medical-history/options";
import {
  capitalizeFirstLetter,
  formatWordsIntoLines,
  normalizeSourceValue,
} from "../../helpers/functions";
import { MedicalHistoryTypes } from "../patient/clinical-services/service-steps/clinical-assessment/data";
import { ClinicalServiceTypes } from "../patient/text/data";
import RootModalWithAction from "../common/RootModalWithAction";
import CustomInput from "../common/CustomInput";
import { SymptomControlOptions } from "../patient/create/options";
import { useClickOutsideTooltip } from "../../hooks/useClickOutsideTooltip";

const MedicalConditionListPopup = ({
  title,
  open,
  handleClose,
  patientId,
  conditions = [],
}) => {
  const [idToDelete, setIdToDelete] = useState(null);
  const [idToRestore, setIdToRestore] = useState(null);
  const [openDeleteHistory, setOpenDeleteHistory] = useState(false);
  const [openRestoreHistory, setOpenRestoreHistory] = useState(false);

  const [showCreateSlider, setShowCreateSlider] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [create, setCreate] = useState(true);
  const [localCondition, setLocalCondition] = useState(
    conditions?.map((x) => ({ ...x })) || []
  );
  const [statusFilter, setStatusFilter] = useState("all");
  const [showFooterActions, setShowFooterActions] = useState(false);
  const [openTooltipId, setOpenTooltipId] = useState(null);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const tooltipWrapperRef = useClickOutsideTooltip(openTooltipId, setOpenTooltipId);
  const conditionSnapshotRef = React.useRef("");
  const prevDrawerStateRef = React.useRef(showCreateSlider);
  const prevConditionsKeyRef = React.useRef("");
  const prevConditionsRef = React.useRef([]);
  const menuOpenStateRef = React.useRef(new Map());
  const dispatch = useDispatch();
  const { serviceId } = useParams();
  const { clinicalServiceName, clinicalServiceObj, clinicalServicesSmall } = useSelector(
    (state) => state.clinicalService
  );

  const inProgressServices = React.useMemo(() => {
    const services = [];
    const getConfirmedConditions = (service) =>
      service.initialAssessment?.initialAssessmentForm?.clinicalAssessment?.confirmedMedicalConditions ?? [];

    [clinicalServicesSmall?.minorAilments, clinicalServicesSmall?.medicationReviews].forEach((serviceList, index) => {
      if (serviceList) {
        serviceList
          .filter((s) => s.status === "inProgress")
          .forEach((service) => {
            services.push({
              id: service.id,
              type: index === 0 ? "minorAilment" : "medicationReview",
              confirmedMedicalConditions: getConfirmedConditions(service),
            });
          });
      }
    });

    if (serviceId && clinicalServiceName && !services.some((s) => s.id === serviceId) && clinicalServiceObj) {
      services.push({
        id: serviceId,
        type: clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis ? "minorAilment" : "medicationReview",
        confirmedMedicalConditions: getConfirmedConditions(clinicalServiceObj),
      });
    }

    return services;
  }, [clinicalServicesSmall, serviceId, clinicalServiceName, clinicalServiceObj]);

  const updateClinicalServicesWithMedicalConditions = React.useCallback(
    (confirmedConditionIds) => {
      if (inProgressServices.length === 0) return;
      const updatedSteps = { clinicalAssessment: { confirmedMedicalConditions: confirmedConditionIds } };
      inProgressServices.forEach((service) => {
        const updateAction = service.type === "minorAilment" ? updateClinicalService : updateMedicationReview;
        dispatch(updateAction(service.id, updatedSteps));
      });
    },
    [inProgressServices, dispatch]
  );

  const findNewActiveConditions = React.useCallback((currentConditions, previousConditions) => {
    const previousIds = new Set(previousConditions.map((c) => c.id));
    return currentConditions.filter((c) => c.status === "active" && !previousIds.has(c.id)).map((c) => c.id);
  }, []);

  useEffect(() => {
    const sorted = [...(conditions || [])].sort((a, b) => String(a.id).localeCompare(String(b.id)));
    const key = JSON.stringify(sorted);
    if (key !== prevConditionsKeyRef.current) {
      setLocalCondition(sorted.map((x) => ({ ...x })));
      prevConditionsKeyRef.current = key;
    }
  }, [conditions]);

  useEffect(() => {
    const wasOpen = prevDrawerStateRef.current;
    prevDrawerStateRef.current = showCreateSlider;
    if (!showCreateSlider && wasOpen && patientId) {
      dispatch(fetchMedicalConditions(patientId));
    }
  }, [showCreateSlider, patientId, dispatch]);

  useEffect(() => {
    if (open && patientId) {
      dispatch(fetchMedicalConditions(patientId));
    }
  }, [open, patientId, dispatch]);

  useEffect(() => {
    if (open && conditions) {
      prevConditionsRef.current = conditions || [];
    }
  }, [open, conditions]);

  useEffect(() => {
    if (!showCreateSlider && conditionSnapshotRef.current) {
      const snapshotIds = new Set(JSON.parse(conditionSnapshotRef.current).map((c) => String(c.id)));
      const currentActive = (conditions || []).filter((c) => c.status !== "inactive").sort((a, b) => String(a.id).localeCompare(String(b.id)));
      if (currentActive.some((c) => !snapshotIds.has(String(c.id)))) {
        setShowFooterActions(true);
      }
    }
  }, [conditions, showCreateSlider]);

  useEffect(() => {
    if (!open) {
      setShowFooterActions(false);
      setShowCreateSlider(false);
      setSelectedHistory(null);
      setCreate(true);
      setOpenTooltipId(null);
      setOpenMenuIndex(null);
      conditionSnapshotRef.current = "";
      prevConditionsRef.current = [];
      menuOpenStateRef.current.clear();
    }
  }, [open]);
  
  useEffect(() => {
    const sliderJustClosed = prevDrawerStateRef.current && !showCreateSlider;
    if (!showCreateSlider && conditions?.length) prevConditionsRef.current = [...conditions];
    if (!sliderJustClosed || !conditions?.length || inProgressServices.length === 0) return;

    const newActiveConditionIds = findNewActiveConditions(conditions, prevConditionsRef.current);
    if (newActiveConditionIds.length === 0) return;

    inProgressServices.forEach((service) => {
      const existingConfirmed = service.confirmedMedicalConditions || [];
      const idsToAdd = newActiveConditionIds.filter((id) => !existingConfirmed.includes(id));
      if (idsToAdd.length > 0) {
        const updatedSteps = { clinicalAssessment: { confirmedMedicalConditions: [...existingConfirmed, ...idsToAdd] } };
        const updateAction = service.type === "minorAilment" ? updateClinicalService : updateMedicationReview;
        dispatch(updateAction(service.id, updatedSteps));
      }
    });

    prevConditionsRef.current = [...conditions];
  }, [showCreateSlider, conditions, inProgressServices, dispatch, findNewActiveConditions]);

  const lastUpdatedDate = conditions
    ?.map((condition) => condition.confirmedDate)
    .sort((a, b) => new Date(b) - new Date(a))[0];


  const updateCondition = async (id, status) => {
    const condition = localCondition.find((item) => item.id === id);
    if (!condition) return;

    const currentCondition = {
      medicalConditions: [
        {
          id: condition.id,
          name: condition.conditionName,
          source: normalizeSourceValue(condition.source),
          comments: condition.comments,
          diagnosisDate: new Date(),
          overrideDate: new Date(),
          commentsToClinicalAlert: false,
          medicalConditionGenCode: condition.medicalConditionGenCode,
          status: status,
          previousStatus: condition.status,
        },
      ],
    };

    await dispatch(updateMedicalConditionFromChart(patientId, currentCondition));
    
    setLocalCondition((prev) =>
      prev.map((c) => (c.id === id ? { ...c, status } : c))
    );
    setShowFooterActions(true);
  };

  const confirmAllMedicalConditions = () => {
    const activeConditions = localCondition.filter(
      (condition) => condition.status === "active"
    );

    const confirmedConditionIds = activeConditions.map((condition) => condition.id);
    
    updateClinicalServicesWithMedicalConditions(confirmedConditionIds);
  };

  const manageClick = () => {
    document.body.classList.toggle("overflow-hidden");
  };

  const displayConditions = (localCondition || []).filter((val) =>
    statusFilter === "all" ? true : val.status === statusFilter
  );

  const shouldShowScrollbar = displayConditions.length > 6;

  return (
    <>
      <RootModalWithAction
        open={open}
        handleClose={handleClose}
        className={`!min-w-[1140px] m-4`}
        title={title}
        titleClassName="text-3xl text-teal-300"
        subTitle=""
        subTitleClassName={` text-sm-body text-teal-600 `}
        headClassName="px-5 py-3"
        showAdd={false}
        hideBorder={true}
        headerLayout="compact"
        hideCloseIcon={showFooterActions}
        closeOnOutsideClick={false}
        renderCompactActions={({ CloseButton }) => (
          <div className="flex items-center gap-3">
            <CustomInput
              options={[
                { value: "all", label: "All" },
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
              ]}
              name="condition-filter"
              className="font-semibold bg-white border border-gray-100 text-body-md !w-[220px] !h-[45px] rounded-full"
              type="dropdown"
              showOptionalLabel={false}
              value={statusFilter}
              onChange={(option) => setStatusFilter(option.value)}
            />
            <button
              className="disabled:opacity-40 min-w-[220px] h-[45px] rounded-full bg-REELTeal border-REELTeal hover:bg-teal-200 text-white inline-flex border transition-all duration-300 items-center font-semibold justify-center px-5"
              onClick={() => {
                const active = (conditions || [])
                  .filter((c) => c.status !== "inactive")
                  .sort((a, b) => String(a.id).localeCompare(String(b.id)));
                conditionSnapshotRef.current = JSON.stringify(active);
                setShowFooterActions(false);
                setShowCreateSlider(true);
              }}
            >
              Add Medical Condition
            </button>
            {CloseButton}
          </div>
        )}
      >
        <div className="relative flex flex-col items-start gap-0 px-5 w-[1140px] flex-shrink-0 overflow-hidden">
          <div
            className={`w-full flex-1 ${
              shouldShowScrollbar ? "overflow-y-auto scrollbar-hide" : "overflow-y-visible"
            }`}
            style={{
              ...(shouldShowScrollbar ? { maxHeight: "290px" } : {}),
              marginBottom: !showFooterActions
                ? "20px"
                : "0px",
            }}
          >
            <table className="w-full border border-gray-20">
              <thead className="bg-gray-20">
                <tr className="text-body-md h-[50px] px-6 py-3 flex items-center gap-2 self-stretch">
                  <th
                    scope="col"
                    className="font-semibold text-left text-teal-300 w-[45%]"
                  >
                    Medical Conditions
                  </th>
                  <th
                    scope="col"
                    className="font-semibold text-left text-teal-300 w-[25%]"
                  >
                    Symptom Control
                  </th>
                  <th
                    scope="col"
                    className="font-semibold text-center text-teal-300 w-[15%]"
                  >
                    Comments
                  </th>
                  <th
                    scope="col"
                    className="font-semibold text-center text-teal-300 w-[15%]"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 text-body-md">
              {displayConditions.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="text-center py-4 font-medium text-gray-900"
                  >
                    No Medical Condition Found
                  </td>
                </tr>
              ) : (
                displayConditions
                  .sort((a, b) => a.conditionName?.localeCompare(b.conditionName))
                  .map((item, i) => {
                    const isStripedRow = i % 2 === 1;
                    const rowBgClass = item.status == "inactive" ? "bg-red-50" : isStripedRow ? "bg-gray-10" : "bg-white";
                    return (
                    <tr
                      className={`transition-colors duration-300 cursor-pointer group hover:bg-blue-100 h-[40px] px-6 py-4 flex items-center gap-3 self-stretch border-b border-gray-50 ${rowBgClass}`}
                      key={i}
                    >
                      <td className="font-medium text-teal-300 capitalize w-[45%]">
                        {capitalizeFirstLetter(item?.conditionName || item?.name)}
                      </td>
                      <td className="text-gray-700 capitalize w-[25%]">
                        {(() => {
                          const symptomControl = item?.symptomControlled;
                          const foundOption = SymptomControlOptions.find(
                            (val) => val.value === symptomControl?.toString()
                          );
                          // Show "Not Specified" for null, undefined, or empty string (for backward compatibility)
                          return foundOption?.label || (symptomControl == null || symptomControl === "" ? "Not Specified" : "");
                        })()}
                      </td>
                      <td className="flex items-center justify-center w-[15%]">
                        {item.comments && (
                          <div className="flex justify-center w-full items-center pt-2">
                            <div 
                              ref={openTooltipId === `condition_comments_${item.id ?? i}` ? tooltipWrapperRef : null}
                              className="relative inline-flex cursor-pointer"
                              data-tooltip-id={openTooltipId === `condition_comments_${item.id ?? i}` ? undefined : `condition_hover_tooltip_${item.id ?? i}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                const tooltipId = `condition_comments_${item.id ?? i}`;
                                setOpenTooltipId(openTooltipId === tooltipId ? null : tooltipId);
                              }}
                            >
                              <ReactSVG
                                src="/icons/paper-clip-new.svg"
                                className="w-6"
                              />
                              {openTooltipId !== `condition_comments_${item.id ?? i}` && (
                                <NewToolTip
                                  id={`condition_hover_tooltip_${item.id ?? i}`}
                                  render={() => (
                                    <div className="bg-white p-2 text-teal-300 text-body-sm">
                                      Click to view comments
                                    </div>
                                  )}
                                />
                              )}
                              <NewToolTip
                                id={`condition_comments_${item.id ?? i}`}
                                isOpen={openTooltipId === `condition_comments_${item.id ?? i}`}
                                render={() => (
                                  <div className="bg-white max-w-[460px]">
                                    <div className="flex flex-col gap-3 p-2">
                                      <div className="text-teal-300 text-sm-body prose-a:underline">
                                        <div className="flex flex-col gap-4">
                                          <p style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                            {formatWordsIntoLines(item.comments)}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                clickable={true}
                                handleHide={() => setOpenTooltipId(null)}
                                anchorSelect={`[data-tooltip-id="condition_hover_tooltip_${item.id ?? i}"]`}
                              />
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-1 py-4 text-gray-800 whitespace-nowrap w-[15%]">
                        {item.status !== "inactive" && (
                          <div className="flex justify-center">
                            <Menu
                              as="div"
                              className="relative inline-block text-left"
                            >
                              {({ open }) => {
                                // Track menu state and defer state update to avoid setState during render
                                if (open !== menuOpenStateRef.current.get(i)) {
                                  menuOpenStateRef.current.set(i, open);
                                  requestAnimationFrame(() => setOpenMenuIndex(open ? i : null));
                                }
                                
                                // Hide icon if menu above is open
                                const shouldHideIcon = menuOpenStateRef.current.get(i - 1) || openMenuIndex === i - 1;
                                
                                return (
                                  <>
                                    <div>
                                      <Menu.Button>
                                        <ReactSVG
                                          src={`/icons/triple-dot.svg`}
                                          className={`flex text-black !items-center w-6 scale-x-[1.4] scale-y-[1.4] ${shouldHideIcon ? 'opacity-0 pointer-events-none' : ''}`}
                                        />
                                      </Menu.Button>
                                    </div>
                                    <Transition
                                      as={Fragment}
                                      enter="transition ease-out duration-100"
                                      enterFrom="transform translate-y-2 opacity-0 scale-95"
                                      enterTo="transform translate-y-0 opacity-100 scale-100"
                                      leave="transition ease-in duration-75"
                                      leaveFrom="transform translate-y-0 opacity-100 scale-100"
                                      leaveTo="transform translate-y-2 opacity-0 scale-95"
                                    >
                                      <Menu.Items
                                        className={
                                          i === displayConditions.length - 1 || displayConditions.length === 1
                                            ? "absolute left-[-6px] min-w-[70px] w-auto origin-bottom bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 bottom-full mb-0"
                                            : "absolute left-[-6px] min-w-[70px] w-auto origin-top bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 top-full mt-0"
                                        }
                                      >
                                        <Menu.Item>
                                          <button
                                            className="block w-full text-left h-[30px] px-2.5 py-2.5 flex items-center gap-2 hover:bg-gray-50"
                                            onClick={(e) => {
                                              // Don't take snapshot for edit - footer actions only for adding new conditions
                                              setShowFooterActions(false);
                                              conditionSnapshotRef.current = "";
                                              setSelectedHistory(item);
                                              setCreate(false);
                                              setShowCreateSlider(true);
                                            }}
                                          >
                                            Edit
                                          </button>
                                        </Menu.Item>
                                        <Menu.Item>
                                          <button
                                            className="block w-full text-left h-[30px] px-2.5 py-2.5 flex items-center gap-2 hover:bg-gray-50"
                                            onClick={() => {
                                              setSelectedHistory(item);
                                              setOpenDeleteHistory(true);
                                              setIdToDelete(item.id);
                                            }}
                                          >
                                            Delete
                                          </button>
                                        </Menu.Item>
                                      </Menu.Items>
                                    </Transition>
                                  </>
                                );
                              }}
                            </Menu>
                          </div>
                        )}
                        {item.status === "inactive" && (
                          <div className="flex justify-start pl-12">
                            <ReactSVG
                              src="/icons/undo.svg"
                              className="w-6"
                              onClick={() => {
                                setOpenRestoreHistory(true);
                                setIdToRestore(item.id);
                                setSelectedHistory(item);
                              }}
                            />
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        {showFooterActions ? (
          <div className="flex justify-end gap-3 px-6 pb-5">
            <button
              className="inline-flex min-w-[220px] disabled:opacity-50 rounded-[24px] border transition-all duration-300 text-body-md items-center font-semibold justify-center px-4 py-2 bg-REELTeal border-REELTeal text-white hover:bg-teal-200"
              onClick={async () => {
                await confirmAllMedicalConditions();
                setShowFooterActions(false);
                conditionSnapshotRef.current = "";
                prevConditionsRef.current = [];
                handleClose();
              }}
            >
              Confirm Medical Condition List
            </button>
          </div>
        ) : (
          // Spacer to provide visual breathing room when no footer actions are shown
          <div className="px-6 pb-4" />
        )}
      </RootModalWithAction>
      <ConfirmationModalNew
        type="primary"
        title="Delete Medical Condition"
        className="z-[100]"
        content={() =>
          `Are you sure you want to delete ${capitalizeFirstLetter(
            selectedHistory?.conditionName ?? ""
          )}?`
        }
        confirmAction={() => {
          updateCondition(idToDelete, "inactive");
          setIdToDelete(null);
          setSelectedHistory(null);
          setOpenDeleteHistory(false);
        }}
        optionLabel="Reason"
        requireInput={true}
        open={openDeleteHistory}
        handleClose={() => {
          setSelectedHistory(null);
          setOpenDeleteHistory(false);
          setIdToDelete(null);
        }}
        onCross={() => {
          // setSelectedHistory(null);
          setOpenDeleteHistory(false);
          setIdToDelete(null);
          setSelectedHistory(null);
        }}
        options={DeleteOptions}
      />
      <ConfirmationModalNew
        open={openRestoreHistory}
        title="Restore Medical Condition"
        type="primary"
        confirmText="Yes"
        content={() => (
          <p>
            Are you sure you wish to restore {capitalizeFirstLetter(
              selectedHistory?.conditionName || localCondition.find(c => c.id === idToRestore)?.conditionName || "Medical Condition"
            )}?
          </p>
        )}
        onCross={() => {
          setOpenRestoreHistory(false);
          setIdToRestore(null);
          setSelectedHistory(null);
        }}
        handleClose={() => {
          setOpenRestoreHistory(false);
          setIdToRestore(null);
          setSelectedHistory(null);
        }}
        confirmAction={() => {
          updateCondition(idToRestore, "active");
          setOpenRestoreHistory(false);
          setIdToRestore(null);
          setSelectedHistory(null);
        }}
      />
      <MedicalHistoryCreate
        open={showCreateSlider}
        isFromPatientBar={true}
        patientMedicalConditions={conditions}
        onClose={() => {
          setShowCreateSlider(false);
          manageClick();
          setSelectedHistory(null);
          setCreate(true);
        }}
        selectedHistoryType={MedicalHistoryTypes.Condition}
        onSave={async () => {
          if (patientId) {
            await dispatch(fetchMedicalConditions(patientId));
            await dispatch(fetchClinicalServices(patientId));
          }
          setShowFooterActions(true);
        }}
        selectedHistory={
          selectedHistory !== null
            ? { ...selectedHistory, name: selectedHistory?.conditionName }
            : null
        }
        patientId={patientId}
        create={create}
      />
    </>
  );
};

export default MedicalConditionListPopup;
